import React from 'react';
import { ScanIcons } from 'src/constants/general-media';
import { AlertLevels } from 'src/models/alert-model';
import { DoorScanResult, DoorScanResultType, ScanResultType, Trailer } from 'src/models/unload';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import UnloadScanStep, { getFailureMessage, NOT_ENABLED_MESSAGE, ORPHAN_ENDED_MESSAGE,
    ScanResultMessage } from 'src/components/pages/unload-page/common/UnloadScanStep';
import { getBoundContainerName } from 'src/utils/unload-scan-results';

// A Record, so an outcome without a message fails to compile
const MESSAGE_BY_RESULT_TYPE: Record<DoorScanResultType, (scan: DoorScanResult) => ScanResultMessage | undefined> = {
    [ScanResultType.ACCEPTED]: () => undefined,
    [ScanResultType.NO_TRAILER]: () => ({ type: AlertLevels.INFORMATIONAL, title: AppStrings.UNLOAD_NO_TRAILER_TITLE,
        lines: [AppStrings.UNLOAD_NO_TRAILER_BODY] }),
    [ScanResultType.NOT_ENABLED]: () => NOT_ENABLED_MESSAGE,
    [ScanResultType.REFUSED]: getFailureMessage,
    [ScanResultType.SYSTEM_ERROR]: getFailureMessage
};

// A trailer or pallet just closed, by name, with the count its loop last showed; a count never read is left out
const getClosedMessage = (trailer: Trailer): ScanResultMessage => ({ type: AlertLevels.SUCCESS,
    title: trailer.palletId ? AppStrings.UNLOAD_PALLET_CLOSED_TITLE : AppStrings.UNLOAD_CLOSED_TITLE,
    lines: [[getBoundContainerName(trailer),
        ...(trailer.count === undefined ? [] : [trailer.count + AppStrings.UNLOAD_COUNT_SUFFIX])]
        .join(AppStrings.UNLOAD_SEPARATOR)] });

interface UnloadDoorScanPageProps {
    // The pallet step, in staging
    staging?: boolean;
    // The latest scan on this step, with its result
    lastScan?: DoorScanResult;
    // Shown until the next scan
    closed?: Trailer;
    // End Orphan just returned to this step, which says so until the next scan
    orphanEnded?: boolean;
    // Takes each scan
    onSubmit: (value: string) => void;
}

// What the step asks for: a dock door, or on /unload-pallet a pallet
const DOOR_STEP = { icon: ScanIcons.SCAN_TRAILER_ICON, heading: AppStrings.UNLOAD_SCAN_DOOR_HEADING,
    fieldTestId: 'unload-door-scan-field', fieldLabel: AppStrings.UNLOAD_DOOR_FIELD_LABEL,
    placeholder: AppStrings.UNLOAD_DOOR_FIELD_PLACEHOLDER };
const PALLET_STEP = { icon: ScanIcons.SCAN_PALETTE_ICON, heading: AppStrings.UNLOAD_SCAN_PALLET_HEADING,
    fieldTestId: 'unload-pallet-scan-field', fieldLabel: AppStrings.UNLOAD_PALLET_FIELD_LABEL,
    placeholder: AppStrings.UNLOAD_PALLET_FIELD_PLACEHOLDER };

// The latest scan's result, else what was just closed, else that End Orphan returned here
const getDoorStepMessage = ({ lastScan, closed, orphanEnded }: UnloadDoorScanPageProps) => {
    if (lastScan) return MESSAGE_BY_RESULT_TYPE[lastScan.outcome](lastScan);
    if (closed) return getClosedMessage(closed);
    return orphanEnded ? ORPHAN_ENDED_MESSAGE : undefined;
};

const UnloadDoorScanPage = (props: UnloadDoorScanPageProps) => (
    <UnloadScanStep {...(props.staging ? PALLET_STEP : DOOR_STEP)} message={getDoorStepMessage(props)}
                    onSubmit={props.onSubmit}/>
);

export default UnloadDoorScanPage;
